import React, { useEffect, useState } from 'react'

/* Atoms */
import Alert from 'components_new/atoms/Alert'
import Button from 'components_new/atoms/Button'
import TextField from 'components_new/atoms/TextField'

/* Molecules */
import FormSection from 'components_new/molecules/FormSection'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

/* Organisms */

/* Actions */
import { useExportTableActions } from 'redux/actions/ExportTables'
import { useExportTables } from 'redux/hooks/ExportTables'

/* Hooks */
import { useTranslation } from 'language/translator'

/* Types */
import { TExportTableWithWidget } from 'types/GlobalExportTable'

/* Local files */
import { getExportTableEndpoint } from './utils'

interface SlugDialogProps {
  exportTable: TExportTableWithWidget
  onClose: () => void
  open: boolean
  variant: 'edit' | 'activate'
}

const sanitizeSlug = (value: string): string => {
  return value
    .toLowerCase()
    .replace(/[äå]/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/ /g, '-')
    .replace(/[^a-z-]/g, '')
}

const isValidSlug = (value: string): boolean => {
  return /^[a-z-]+$/.test(value) && value.length > 0
}

export const SlugDialog = (props: SlugDialogProps) => {
  const { exportTable, onClose, open, variant } = props

  const [slug, setSlug] = useState('')

  useEffect(() => {
    if (open) {
      setSlug(exportTable.slug ?? '')
    }
  }, [open])

  const { t } = useTranslation()

  const { tryUpdateExportTable } = useExportTableActions()

  const exportTables = useExportTables()

  const isSlugTaken = exportTables
    .filter((table) => table.id !== exportTable.id)
    .map((table) => table.slug)
    .includes(slug)
  const isSlugEmpty = slug.length === 0
  const isSlugValid = isValidSlug(slug)
  const isSlugChanged = variant === 'edit' && slug !== exportTable.slug
  const canActivate = !isSlugEmpty && isSlugValid && !isSlugTaken

  const getHelperText = () => {
    if (isSlugEmpty) {
      return t('export_tables', 'slug_empty_error')
    }

    if (isSlugTaken) {
      return t('export_tables', 'slug_taken_error')
    }

    if (!isSlugValid) {
      return t('export_tables', 'slug_invalid_error')
    }

    return ''
  }

  return (
    <SimpleDialog
      actions={
        <>
          <Button onClick={onClose} variant={'text'}>
            {t('global', 'cancel')}
          </Button>
          <Button
            disabled={!canActivate}
            onClick={() => {
              tryUpdateExportTable(exportTable.id, { data: { slug } })

              onClose()
            }}
            variant={'outlined'}
          >
            {variant === 'edit' ? t('global', 'save') : t('global', 'activate')}
          </Button>
        </>
      }
      title={
        variant === 'edit'
          ? t('export_tables', 'edit_slug')
          : t('export_tables', 'activate_api')
      }
      open={open}
      onClose={onClose}
    >
      {variant === 'edit' && isSlugChanged && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('export_tables', 'slug_change_warning')}
        </Alert>
      )}
      <FormSection
        helperText={t('export_tables', 'slug_helper')}
        title={
          variant === 'edit'
            ? t('export_tables', 'new_slug')
            : t('export_tables', 'choose_slug')
        }
      >
        <TextField
          error={!canActivate && !isSlugEmpty}
          helperText={getHelperText()}
          onChange={(e) => setSlug(sanitizeSlug(e.target.value))}
          value={slug}
          fullWidth
          sx={{ mt: 1 }}
        />
      </FormSection>
      <FormSection
        helperText={t('export_tables', 'preview_helper')}
        title={t('export_tables', 'preview_title')}
      >
        <TextField
          value={getExportTableEndpoint(slug)}
          fullWidth
          InputProps={{
            readOnly: true
          }}
          sx={{ mt: 1 }}
        />
      </FormSection>
    </SimpleDialog>
  )
}
